import React from 'react';
import { Link } from 'react-router-dom';
import { X, MapPin, Building2, Tag, Globe, Phone, Mail, Star, Award, Heart, Share2, Navigation, ExternalLink } from 'lucide-react';

export default function BoothInfoPanel({ booth, onClose, onNavigate }) {
  if (!booth) return null;

  const statusColors = {
    occupied: 'bg-emerald-500',
    available: 'bg-slate-400',
    reserved: 'bg-amber-500',
  };

  return (
    <div 
      className="fixed right-6 top-6 bottom-6 w-96 bg-slate-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-700/50 overflow-hidden z-50 animate-in slide-in-from-right duration-300"
      data-testid="booth-info-panel"
    >
      {/* Header with gradient */}
      <div 
        className="relative p-6 pb-8"
        style={{
          background: `linear-gradient(135deg, ${booth.color || '#3498db'} 0%, ${booth.color || '#3498db'}88 100%)`,
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
          data-testid="close-info-panel-btn"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${statusColors[booth.status]}`}>
            {booth.status?.toUpperCase()}
          </span>
          {booth.is_premium && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500 text-black">
              PREMIUM
            </span>
          )}
          {booth.is_corner && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500 text-white">
              CORNER
            </span>
          )}
        </div>

        <h2 className="text-3xl font-black text-white mb-1" data-testid="booth-number-title">
          {booth.booth_number}
        </h2>
        <p className="text-white/80 text-sm">
          {booth.hall?.name || 'Outdoor Zone'}
        </p>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[calc(100vh-320px)]">
        {booth.company ? (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-black text-xl">
                  {booth.company.name?.[0] || 'C'}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" data-testid="company-name">
                    {booth.company.name}
                  </h3>
                  <p className="text-slate-400 text-sm">{booth.company.country}</p>
                </div>
              </div>

              <p className="text-slate-300 text-sm leading-relaxed" data-testid="company-description">
                {booth.company.description}
              </p>
            </div>

            {booth.category && (
              <div className="mb-4 flex items-center gap-2">
                <Tag className="w-4 h-4 text-slate-400" />
                <span 
                  className="px-3 py-1 rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: booth.category.color }}
                >
                  {booth.category.name}
                </span>
              </div>
            )}

            {/* Company details grid */}
            <div className="grid grid-cols-1 gap-3 mb-6">
              {booth.company.email && (
                <a
                  href={`mailto:${booth.company.email}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors border border-slate-700/50"
                  data-testid="company-email-link"
                >
                  <Mail className="w-4 h-4 text-blue-400" />
                  <span className="text-slate-300 text-sm truncate">{booth.company.email}</span>
                </a>
              )}

              {booth.company.phone && (
                <a
                  href={`tel:${booth.company.phone}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors border border-slate-700/50"
                  data-testid="company-phone-link"
                >
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <span className="text-slate-300 text-sm">{booth.company.phone}</span>
                </a>
              )}

              {booth.company.website && (
                <a
                  href={booth.company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors border border-slate-700/50"
                  data-testid="company-website-link"
                >
                  <Globe className="w-4 h-4 text-purple-400" />
                  <span className="text-slate-300 text-sm truncate">{booth.company.website}</span>
                </a>
              )}
            </div>

            {/* Location info */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-red-400" />
                <span className="text-white font-semibold text-sm">Location Details</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-slate-400">Position X:</span>
                  <span className="text-white ml-2">{booth.position_x}</span>
                </div>
                <div>
                  <span className="text-slate-400">Position Z:</span>
                  <span className="text-white ml-2">{booth.position_z}</span>
                </div>
                <div>
                  <span className="text-slate-400">Size:</span>
                  <span className="text-white ml-2">{booth.width}x{booth.depth}m</span>
                </div>
                <div>
                  <span className="text-slate-400">Views:</span>
                  <span className="text-white ml-2">{booth.view_count || 0}</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Available Booth</h3>
            <p className="text-slate-400 text-sm mb-4">
              This booth is available for rent. Contact organizers for more info.
            </p>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <p className="text-slate-300 text-sm">Rate: RM {booth.price?.toLocaleString() || '5,000'} / event</p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <Link 
            to={`/booth/${booth.id}`}
            className="col-span-2 flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold text-sm transition-all shadow-lg shadow-emerald-500/30"
            data-testid="view-details-btn"
          >
            <ExternalLink className="w-4 h-4" /> View Full Details
          </Link>
          <button 
            onClick={() => onNavigate && onNavigate(booth)}
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold text-sm transition-all shadow-lg shadow-blue-500/30"
            data-testid="navigate-btn"
          >
            <Navigation className="w-4 h-4" />
            Take me there
          </button>
          <button 
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition-all border border-slate-700/50"
            data-testid="bookmark-btn"
          >
            <Heart className="w-4 h-4" />
            Bookmark
          </button>
          <button 
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition-all border border-slate-700/50"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <button 
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition-all border border-slate-700/50"
            data-testid="rate-btn"
          >
            <Star className="w-4 h-4" />
            Rate
          </button>
        </div>
      </div>
    </div>
  );
}
